import { useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, Plus, Search, Sparkles } from "lucide-react";
import { ENRICHMENT_VARIABLES, buildVariableToken } from "@/lib/messageVariables";
import { cn } from "@/lib/utils";

interface VariablePickerProps {
  /** Basic + CSV variables available for this campaign. */
  basicVariables: string[];
  /** Advanced enrichment tags are super-admin only. */
  allowEnrichmentVariables?: boolean;
  disabled?: boolean;
  onInsert: (token: string) => void;
}

const BASIC_LABELS: Record<string, string> = {
  first_name: "First name",
  last_name: "Last name",
  company: "Company",
  title: "Job title",
};

const prettify = (name: string) =>
  BASIC_LABELS[name] || name.replace(/_/g, " ").replace(/^\w/, (c) => c.toUpperCase());

/**
 * One entry point for adding a variable. Advanced tags never resolve for every
 * target, so the picker collects the fallback in the same step instead of
 * asking the user to hand-write `{tag|fallback}`.
 */
export function VariablePicker({
  basicVariables,
  allowEnrichmentVariables = false,
  disabled,
  onInsert,
}: VariablePickerProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [pending, setPending] = useState<string | null>(null);
  const [fallback, setFallback] = useState("");

  const standard = ["first_name", "last_name", "company", "title"];
  const groups = useMemo(() => {
    const q = search.trim().toLowerCase();
    const match = (v: string) => !q || v.includes(q) || prettify(v).toLowerCase().includes(q);
    return [
      { key: "basic", label: "Basic", items: basicVariables.filter((v) => standard.includes(v) && match(v)) },
      {
        key: "list",
        label: "From your list",
        items: basicVariables.filter((v) => !standard.includes(v) && match(v)),
      },
      {
        key: "advanced",
        label: "Advanced (pre-fetch)",
        items: allowEnrichmentVariables ? (ENRICHMENT_VARIABLES as readonly string[]).filter(match) : [],
      },
    ].filter((g) => g.items.length > 0);
  }, [basicVariables, allowEnrichmentVariables, search]);

  const reset = () => {
    setPending(null);
    setFallback("");
    setSearch("");
  };

  const choose = (name: string, isEnrichment: boolean) => {
    if (isEnrichment) {
      setPending(name);
      setFallback("");
      return;
    }
    onInsert(buildVariableToken(name));
    setOpen(false);
    reset();
  };

  const confirmAdvanced = () => {
    if (!pending) return;
    onInsert(buildVariableToken(pending, fallback));
    setOpen(false);
    reset();
  };

  return (
    <Popover
      open={open}
      onOpenChange={(o) => {
        setOpen(o);
        if (!o) reset();
      }}
    >
      <PopoverTrigger asChild>
        <Button type="button" variant="outline" size="sm" className="h-8 text-xs" disabled={disabled}>
          <Plus className="mr-1 h-3.5 w-3.5" />
          Insert variable
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-80 p-0">
        {pending ? (
          <div className="space-y-3 p-3">
            <button
              type="button"
              className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
              onClick={() => setPending(null)}
            >
              <ArrowLeft className="h-3 w-3" /> Back
            </button>
            <div className="space-y-1">
              <Label className="text-xs font-medium">Fallback for {prettify(pending)}</Label>
              <p className="text-[11px] text-muted-foreground">
                Optional — leave empty to skip the phrase when there is no data.
              </p>
            </div>
            <Input
              autoFocus
              value={fallback}
              placeholder="e.g. your team"
              onChange={(e) => setFallback(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  confirmAdvanced();
                }
              }}
              className="h-8 text-sm"
            />
            <div className="rounded-md bg-muted/50 p-2 text-[11px] text-muted-foreground">
              Inserts <code className="font-mono">{buildVariableToken(pending, fallback)}</code>
            </div>
            <Button size="sm" className="w-full" onClick={confirmAdvanced}>
              Insert
            </Button>
          </div>
        ) : (
          <div className="p-2">
            <div className="relative mb-2">
              <Search className="absolute left-2 top-2 h-3.5 w-3.5 text-muted-foreground" />
              <Input
                autoFocus
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search variables..."
                className="h-8 pl-7 text-xs"
              />
            </div>
            <div className="max-h-72 space-y-3 overflow-y-auto pr-1">
              {groups.length === 0 && (
                <p className="p-2 text-xs text-muted-foreground">No variables match that search.</p>
              )}
              {groups.map((group) => (
                <div key={group.key} className="space-y-1">
                  <p className="px-1 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
                    {group.label}
                  </p>
                  {group.items.map((v) => (
                    <button
                      key={v}
                      type="button"
                      onClick={() => choose(v, group.key === "advanced")}
                      className={cn(
                        "flex w-full items-center justify-between gap-2 rounded-md px-2 py-1.5 text-left text-xs hover:bg-muted"
                      )}
                    >
                      <span className="flex items-center gap-1.5">
                        {group.key === "advanced" && <Sparkles className="h-3 w-3 text-primary" />}
                        {prettify(v)}
                      </span>
                      <Badge variant="outline" className="font-mono text-[10px]">
                        {`{${v}}`}
                      </Badge>
                    </button>
                  ))}
                </div>
              ))}
            </div>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
